import Loading from '@/common/atoms/Loading'
import TextErrors from '@/common/atoms/TextErrors'
import DecisionEditModal from '@/decision/modals/DecisionEditModal'
import DecisionModal from '@/decision/modals/DecisionModal'
import useOrgMember from '@/member/hooks/useOrgMember'
import useCirclePermissions from '@/circle/hooks/useCirclePermissions'
import ProposalModal from '@/proposal/modals/ProposalModal'
import { Button, Text, useDisclosure } from '@chakra-ui/react'
import { useCircleDecisionsSubscription } from '@gql'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateIcon } from 'src/icons'
import DecisionItem from '../../decision/components/DecisionItem'

interface Props {
  circleId: string
}

export default function CircleDecisions({ circleId }: Props) {
  const { t } = useTranslation()
  const isMember = useOrgMember()
  // A policy obeys the right to edit its circle (GOUV-14). Until now this
  // button was offered to ANY member of the org, in every governance mode.
  const { canEditRole } = useCirclePermissions(circleId)

  // Subscribe to decisions
  const { data, error, loading } = useCircleDecisionsSubscription({
    variables: { circleId, active: true },
  })
  const decisions = data?.decision

  // Modals
  const [createdId, setCreatedId] = useState<string | undefined>()
  const showModal = useDisclosure()
  const editModal = useDisclosure()
  const proposalModal = useDisclosure()

  const handleOpen = (id: string) => {
    setCreatedId(id)
    showModal.onOpen()
  }

  return (
    <>
      {isMember && (
        <Button
          size="sm"
          mb={4}
          colorScheme="blue"
          leftIcon={<CreateIcon size={20} />}
          onClick={canEditRole ? editModal.onOpen : proposalModal.onOpen}
        >
          {t(
            canEditRole ? 'CircleDecisions.create' : 'CircleDecisions.propose'
          )}
        </Button>
      )}

      {loading && <Loading active size="md" />}
      <TextErrors errors={[error]} />

      {decisions?.length === 0 && (
        <Text fontStyle="italic">{t('CircleDecisions.empty')}</Text>
      )}

      {decisions?.map((decision) => (
        <DecisionItem key={decision.id} decision={decision} />
      ))}

      {editModal.isOpen && (
        <DecisionEditModal
          defaults={{
            circleId,
          }}
          isOpen
          onClose={editModal.onClose}
          onCreate={handleOpen}
        />
      )}

      {showModal.isOpen && createdId && (
        <DecisionModal id={createdId} isOpen onClose={showModal.onClose} />
      )}

      {/* No target: an accepted proposal creates the policy in the registry,
          which is how policies have always been born (GOUV-14). */}
      {proposalModal.isOpen && (
        <ProposalModal
          circleId={circleId}
          isOpen
          onClose={proposalModal.onClose}
        />
      )}
    </>
  )
}
